'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { useProfile } from '@/hooks/use-profile';
import { TWork } from '@/features/fanfic-page/types/fanfic';
import CollectionDropdown from '@/components/collection-dropdown';
import { DonationButton } from '@/components/donation-button';
import { FanficDropdown } from '@/components/ui/fanfic-dropdown';
import { Title } from '@/components/ui/typography';
import { RelationshipGenIcon } from '@/components/icons/relationship-gen';
import { RelationshipIcon } from '@/components/icons/relationship';
import UserIcon from '@/components/icons/user';
import { ClosedEyeIcon } from '@/components/icons/closed-eye';
import { cn } from '@/lib/utils';
import { getRandomPlaceholder } from '@/lib/fanfic-placeholders';
import { getFanficStatus } from '@/features/fanfic-page/utils/getFanficStatus.util';
import { renderStatusIcon } from '@/features/fanfic-page/utils/renderStatusIcon.util';
import { hasAgeConfirmation } from '@/features/age-verification/utils';
import { useAgeConfirmationStore } from '@/features/age-verification/store';
import { useCloudPaymentsPayment } from '@/features/cloudpayments';
import CheckIcon from '@/components/icons/check';
import { parseTextWithLinks } from '@/features/fanfic-page/utils';
import type { BreadcrumbItemData } from '@/components/Breadcrumb/breadcrumb';
import { Breadcrumb } from '@/components/Breadcrumb/breadcrumb';
import { breadcrumbClearBgStyle } from '@/components/Breadcrumb/breadcrumb-clear-bg.style';
import { useModalStore } from '@/store';
import { useAuth } from '@/hooks/use-auth';

export interface HeroSectionProps {
  fanfic: TWork;
  preview: boolean;
  breadcrumbItems?: BreadcrumbItemData[];
  collectionData: {
    isInCollection: boolean;
    rating?: number;
    status?: string;
    isPrivate?: boolean;
    private?: boolean;
  };
  updateRating: (rating: number) => void;
  updateStatus: (status: string, isPrivate?: boolean) => void;
  addToCollection: (status: string, isPrivate?: boolean) => void;
  removeFromCollection: () => void;
  handleReadClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  handleAuxClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  readerUrl: string;
  firstChapterShouldIndexing?: boolean;
}

export function HeroSection({
  fanfic,
  preview,
  breadcrumbItems,
  collectionData,
  updateStatus,
  addToCollection,
  removeFromCollection,
  handleReadClick,
  handleAuxClick,
  readerUrl,
  firstChapterShouldIndexing = true,
}: HeroSectionProps) {
  const { isAuthenticated, user } = useAuth();
  const { userData } = useProfile();
  const { checkAndExecute, hasLocalConfirmation } = useAgeConfirmationStore();
  const cover = fanfic.heroImage?.url || getRandomPlaceholder(fanfic.id);
  const title = fanfic.title || 'Без названия';
  const postStatus = getFanficStatus(fanfic);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const { setIsOpenAuthDialog } = useModalStore();

  const isRating18 = fanfic.rating === '18';

  // Determine if "Read" button link should be followed by search engines
  const shouldIndexing = !isRating18 && firstChapterShouldIndexing;
  const relAttribute = shouldIndexing ? undefined : 'nofollow';
  const isPaidOriginalAndNotPurchased = fanfic.isPaidOriginal && !fanfic.hasPurchased;
  const isPaidOriginalAndPurchased = fanfic.isPaidOriginal && fanfic.hasPurchased;
  const hasConfirmedAge = hasAgeConfirmation(userData) || hasLocalConfirmation;
  const hasTags = !!(
    fanfic.fandom ||
    fanfic.universe ||
    fanfic.additionalUniverses?.length ||
    fanfic.additionalFandoms?.length
  );

  // Use CloudPayments payment hook
  const { handleCloudPaymentsPayment, isProcessingPayment } = useCloudPaymentsPayment({
    postId: fanfic.id,
  });

  const handleBuyOriginalClick = async (mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
    if (isAuthenticated) {
      await handleCloudPaymentsPayment(mouseEvent);
    } else {
      setIsOpenAuthDialog(true, 'Чтобы купить работу, необходимо авторизоваться');
    }
  };

  const handleReadClickWithCheck = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (preview) {
      event.preventDefault();
      return;
    }

    // Keep native browser behavior for open-in-new-tab / modified clicks (SEO + UX).
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    event.preventDefault();
    checkAndExecute(() => handleReadClick(event), isRating18, isAuthenticated, hasConfirmedAge);
  };

  const breadcrumbsNode =
    breadcrumbItems && breadcrumbItems.length > 0 ? (
      <div className="mb-4 min-[1000px]:mb-6" style={breadcrumbClearBgStyle}>
        <Breadcrumb items={breadcrumbItems} />
      </div>
    ) : null;

  return (
    <>
      <div
        className={cn(
          'relative h-auto min-[1000px]:overflow-visible overflow-hidden',
          'min-[1461px]:pb-9',
        )}
        style={{
          backgroundImage: `url(${cover})`,
          backgroundSize: 'cover',
        }}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-2xl" />
        <div className="relative z-10 px-4 h-full max-min-[1000px]:px-2.5">
          <div className="min-[1000px]:px-4 pt-4 min-[1000px]:pt-8 min-[1000px]:flex items-start min-[1000px]:ml-[380px] space-x-4">
            <div className="flex min-[1000px]:hidden flex-col w-full">
              {breadcrumbsNode}
              <div className="flex items-start gap-4">
                <Image
                  src={cover}
                  alt={`Обложка фанфика ${title}`}
                  width={96}
                  height={144}
                  className="w-24 h-36 object-cover rounded-lg shadow-lg flex-shrink-0"
                  priority
                />
                <div className="text-white">
                  <div className="space-y-2">
                    <Title className="text-2xl font-bold text-white">{title}</Title>
                    <div className="flex items-center gap-1 w-full">
                      <div
                        className={`px-2 py-1 rounded-lg shadow-md flex items-center gap-1 bg-[${
                          fanfic.focus === 'het' ? '#505CFF' : '#A84DFF'
                        }]`}
                      >
                        {fanfic.focus === 'het' ? (
                          <RelationshipIcon className="w-3 h-3 text-white" />
                        ) : (
                          <RelationshipGenIcon className="w-3 h-3 text-white" />
                        )}
                        <span className="text-xs font-medium text-white">
                          {fanfic.focus === 'het' ? 'Гет' : 'Джен'}
                        </span>
                      </div>
                      <div className="px-2 py-1 rounded-lg shadow-md flex items-center gap-1 bg-white">
                        <span className="text-xs font-medium text-black">
                          {fanfic.rating ? `${fanfic.rating}+` : '—'}
                        </span>
                      </div>
                      {fanfic.isBlocked && (
                        <div className="px-2 py-1 rounded-lg shadow-md flex items-center gap-1 bg-red-500">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-xs font-medium text-white">Нарушены правила</span>
                        </div>
                      )}
                      {isPaidOriginalAndPurchased && (
                        <div className="px-2 py-1 rounded-lg shadow-md flex items-center gap-1 bg-green-500">
                          <CheckIcon className="w-3 h-3 text-white" />
                          <span className="text-xs font-medium text-white">Куплено</span>
                        </div>
                      )}
                      <FanficDropdown
                        workId={fanfic.id.toString()}
                        fanficTitle={fanfic.title || 'Без названия'}
                        chaptersCount={fanfic.chaptersCount ?? 0}
                        workPathPrefix={fanfic.type === 'original' ? '/originals' : '/fanfics'}
                      />
                    </div>
                  </div>

                  <div className="space-y-1 mt-2">
                    <div className="text-sm font-normal flex items-center gap-1">
                      Автор:{' '}
                      <Link
                        onClick={() => console.log(fanfic.authors)}
                        href={`/author/${fanfic.authors?.[0]?.id}`}
                        className="underline !text-base font-medium flex items-center gap-1"
                      >
                        <UserIcon className="w-3.5 h-3.5 text-[#B4BACD]" />
                        {fanfic.authors?.[0]?.name ?? '—'}
                      </Link>
                    </div>
                    <div className="text-sm flex items-center gap-1">
                      Статус: {postStatus || '—'} {renderStatusIcon(postStatus || '—')}
                    </div>
                    <div className="text-sm">
                      Размер: {fanfic.totalWordCount?.toLocaleString('ru-RU')} слов
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <fieldset
              disabled={preview}
              className="hidden min-[1000px]:flex min-[1000px]:flex-col flex-1 text-white py-4 w-full disabled:pointer-events-none"
            >
              {breadcrumbsNode}
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-[40px] leading-none font-black uppercase text-balance">
                  {title}
                </h1>
                <div className="flex items-center gap-2">
                  <div
                    className={`px-2 py-1 rounded-lg shadow-md flex items-center gap-1 bg-[${
                      fanfic.focus === 'het' ? '#505CFF' : '#A84DFF'
                    }]`}
                  >
                    {fanfic.focus === 'het' ? (
                      <RelationshipIcon className="w-3 h-3 text-white" />
                    ) : (
                      <RelationshipGenIcon className="w-3 h-3 text-white" />
                    )}
                    <span className="text-xs font-medium text-white">
                      {fanfic.focus === 'het' ? 'Гет' : 'Джен'}
                    </span>
                  </div>
                  <div className="px-2 py-1 rounded-lg shadow-md flex items-center gap-1 bg-white">
                    <span className="text-xs font-medium text-black">
                      {fanfic.rating ? `${fanfic.rating}+` : '—'}
                    </span>
                  </div>
                  {fanfic.isBlocked && (
                    <div className="px-2 py-1 rounded-lg shadow-md flex items-center gap-1 bg-red-500">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-xs font-medium text-white">Нарушены правила</span>
                    </div>
                  )}
                  {isPaidOriginalAndPurchased && (
                    <div className="px-2 py-1 rounded-lg shadow-md flex items-center gap-1 bg-green-500">
                      <CheckIcon className="w-3 h-3 text-white" />
                      <span className="text-xs font-medium text-white">Куплено</span>
                    </div>
                  )}
                  <FanficDropdown
                    workId={fanfic.id.toString()}
                    fanficTitle={fanfic.title || 'Без названия'}
                    chaptersCount={fanfic.chaptersCount ?? 0}
                    workPathPrefix={fanfic.type === 'original' ? '/originals' : '/fanfics'}
                  />
                </div>
              </div>

              {hasTags && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {fanfic.universe && (
                    <div className="px-2 py-1 bg-white/20 text-white rounded-lg text-base font-medium">
                      {fanfic.universe.title}
                    </div>
                  )}

                  {!!fanfic?.additionalUniverses?.length &&
                    fanfic.additionalUniverses
                      .filter((universe) => universe.title?.trim())
                      .map((universe) => (
                        <div
                          key={universe.id}
                          className="px-2 py-1 bg-white/20 text-white rounded-lg text-base font-medium"
                        >
                          {universe.title}
                        </div>
                      ))}

                  {fanfic.fandom &&
                    (fanfic.fandom.permanentUrl ? (
                      <Link
                        href={fanfic.fandom.permanentUrl}
                        className="px-2 py-1 bg-white/20 text-white rounded-lg text-base font-medium hover:underline"
                      >
                        {fanfic.fandom.title}
                      </Link>
                    ) : (
                      <div className="px-2 py-1 bg-white/20 text-white rounded-lg text-base font-medium">
                        {fanfic.fandom.title}
                      </div>
                    ))}

                  {!!fanfic?.additionalFandoms?.length &&
                    fanfic.additionalFandoms
                      .filter((fandom) => fandom.title?.trim())
                      .map((fandom) =>
                        fandom.permanentUrl ? (
                          <Link
                            key={fandom.id}
                            href={fandom.permanentUrl}
                            className="px-2 py-1 bg-white/20 text-white rounded-lg text-base font-medium hover:underline"
                          >
                            {fandom.title}
                          </Link>
                        ) : (
                          <div
                            key={fandom.id}
                            className="px-2 py-1 bg-white/20 text-white rounded-lg text-base font-medium"
                          >
                            {fandom.title}
                          </div>
                        ),
                      )}
                </div>
              )}

              <div className="flex items-center gap-6">
                <span className="flex items-center gap-1">
                  <span className="text-[#F2F3F7] text-sm font-normal">Автор: </span>
                  <Link
                    onClick={() => console.log(fanfic.authors)}
                    href={`/author/${fanfic.authors?.[0]?.id}`}
                    className="underline flex text-base font-medium items-center gap-1"
                  >
                    <UserIcon className="w-3.5 h-3.5 text-[#B4BACD]" />
                    {fanfic.authors?.[0]?.name ?? '—'}
                  </Link>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-[#F2F3F7] text-sm font-normal">Статус:</span>{' '}
                  {postStatus || '—'} {renderStatusIcon(postStatus || '—')}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-[#F2F3F7] text-sm font-normal">Размер:</span>{' '}
                  <span className="text-base font-medium">
                    {fanfic.totalWordCount?.toLocaleString('ru-RU')} слов{' '}
                  </span>
                </span>
              </div>
              <fieldset
                disabled={preview}
                className="flex flex-wrap items-center gap-2 w-full mt-4 disabled:pointer-events-none disabled:opacity-60"
              >
                {isPaidOriginalAndNotPurchased ? (
                  <>
                    <button
                      type="button"
                      onClick={handleBuyOriginalClick}
                      disabled={isProcessingPayment}
                      className={cn(
                        'w-56 h-10 flex items-center justify-center p-4 bg-gradient-to-b from-white/20 bg-[#ff3dae] text-white shadow-[0px_10px_30px_0px_rgba(0,0,0,0.25)] py-3 rounded-xl hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed',
                        preview && 'pointer-events-none opacity-60',
                      )}
                    >
                      {isProcessingPayment ? 'Обработка...' : `Купить за ${fanfic.price} ₽`}
                    </button>

                    <Link
                      href={preview || !readerUrl ? '#' : readerUrl}
                      onClick={handleReadClickWithCheck}
                      onAuxClick={handleAuxClick}
                      prefetch={true}
                      rel={relAttribute}
                      className={cn(
                        'bg-white/20 text-white  hover:bg-white/60 py-2.5 px-4 h-10 rounded-xl font-medium text-base flex items-center gap-2 transition-colors whitespace-nowrap',
                        preview && 'pointer-events-none opacity-60',
                      )}
                    >
                      Читать отрывок
                    </Link>
                  </>
                ) : (
                  <Link
                    href={preview || !readerUrl ? '#' : readerUrl}
                    onClick={handleReadClickWithCheck}
                    onAuxClick={handleAuxClick}
                    prefetch={true}
                    rel={relAttribute}
                    className={cn(
                      'w-56 h-10 flex items-center justify-center p-4 bg-gradient-to-b from-white/20 bg-[#ff3dae] text-white shadow-[0px_10px_30px_0px_rgba(0,0,0,0.25)] px-16 py-3 rounded-xl hover:opacity-90',
                      preview && 'pointer-events-none opacity-60',
                    )}
                  >
                    Читать
                  </Link>
                )}

                <CollectionDropdown
                  postId={fanfic.id.toString()}
                  currentStatus={collectionData.status}
                  onStatusChange={(status, isPrivate) => updateStatus(status, isPrivate)}
                  onAddToCollection={(status, isPrivate) => addToCollection(status, isPrivate)}
                  onRemoveFromCollection={removeFromCollection}
                  isInCollection={collectionData.isInCollection}
                  chaptersCount={fanfic.chaptersCount}
                  className="bg-white/20 text-white  hover:bg-white/60 py-2.5 px-4 h-10 rounded-xl font-medium text-base flex items-center gap-2 transition-colors"
                />

                <div className="flex items-center gap-4 ml-auto">
                  {(collectionData.private || collectionData?.isPrivate) && (
                    <div className="bg-white/10 text-white max-h-10 hover:bg-white/20 py-3 px-6 rounded-xl font-medium flex items-center gap-2 transition-colors">
                      Скрыто
                      <ClosedEyeIcon className="w-6 h-6" />
                    </div>
                  )}
                  <DonationButton author={fanfic.authors?.[0] || null} currentUserId={user?.id} />
                </div>
              </fieldset>
            </fieldset>

            <fieldset
              disabled={preview}
              className="min-[1000px]:hidden text-white space-y-4 m-0 mb-4 disabled:pointer-events-none disabled:opacity-60"
              style={{ margin: 0, marginBottom: '16px' }}
            >
              <div>
                <div className="font mb-2 text-sm max-lg:mb-5">Описание:</div>
                <p
                  className={`text-sm leading-relaxed text-pretty ${
                    !showFullDescription ? 'line-clamp-3' : ''
                  }`}
                >
                  {fanfic.description || fanfic.content
                    ? parseTextWithLinks(fanfic.description || fanfic.content || '')
                    : 'Описание пока отсутствует.'}
                </p>
                <button
                  type="button"
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="text-sm underline mt-1 hover:text-gray-300 transition-colors"
                >
                  {showFullDescription ? 'Скрыть' : 'Показать полностью...'}
                </button>
              </div>

              <div className="flex flex-col max-min-[1000px]:!mt-5">
                <div className="flex flex-wrap gap-2 items-center mb-2">
                  {isPaidOriginalAndNotPurchased ? (
                    <>
                      <button
                        type="button"
                        onClick={handleBuyOriginalClick}
                        disabled={isProcessingPayment}
                        className="basis-full max-h-12 px-16 max-min-[1000px]:max-h-10 py-3 max-xs:w-52 max-min-[1000px]:text-sm max-min-[1000px]:py-0 rounded-xl text-white font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{
                          background:
                            'linear-gradient(to bottom, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%), rgba(255, 61, 174, 0.4)',
                          boxShadow:
                            '0px 10px 30px 0px rgba(0,0,0,0.25), inset 0px 0px 90px 0px rgba(255, 61, 174, 1.00)',
                        }}
                      >
                        {isProcessingPayment ? 'Обработка...' : `Купить за ${fanfic.price} ₽`}
                      </button>

                      <Link
                        href={preview || !readerUrl ? '#' : readerUrl}
                        onClick={handleReadClickWithCheck}
                        onAuxClick={handleAuxClick}
                        prefetch={true}
                        rel={relAttribute}
                        className="bg-white/20 text-white  hover:bg-white/60 py-2.5 px-4 h-12 rounded-xl font-medium text-base flex items-center gap-2 transition-colors whitespace-nowrap"
                      >
                        Читать отрывок
                      </Link>
                    </>
                  ) : (
                    <Link
                      href={preview || !readerUrl ? '#' : readerUrl}
                      onClick={handleReadClickWithCheck}
                      onAuxClick={handleAuxClick}
                      prefetch={true}
                      rel={relAttribute}
                      className="basis-full max-h-12 px-16 max-min-[1000px]:max-h-10 py-3 max-xs:w-52 max-min-[1000px]:text-sm max-min-[1000px]:py-0 rounded-xl text-white font-medium transition-all duration-200 hover:opacity-90 flex items-center justify-center"
                      style={{
                        background:
                          'linear-gradient(to bottom, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%), rgba(255, 61, 174, 0.4)',
                        boxShadow:
                          '0px 10px 30px 0px rgba(0,0,0,0.25), inset 0px 0px 90px 0px rgba(255, 61, 174, 1.00)',
                      }}
                    >
                      Читать
                    </Link>
                  )}

                  <CollectionDropdown
                    postId={fanfic.id.toString()}
                    currentStatus={collectionData.status}
                    onStatusChange={(status, isPrivate) => updateStatus(status, isPrivate)}
                    onAddToCollection={(status, isPrivate) => addToCollection(status, isPrivate)}
                    onRemoveFromCollection={removeFromCollection}
                    isInCollection={collectionData.isInCollection}
                    chaptersCount={fanfic.chaptersCount}
                    className="bg-white/20 text-white  hover:bg-white/60 py-2.5 px-4 h-12 rounded-xl font-medium text-base flex items-center gap-2 transition-colors"
                  />
                </div>
                <DonationButton author={fanfic.authors?.[0] || null} currentUserId={user?.id} />
              </div>
            </fieldset>
          </div>
        </div>
      </div>

      <div className="hidden min-[1000px]:block absolute left-8 top-6 z-20">
        <Image
          src={cover}
          alt={`Обложка фанфика ${title}`}
          width={320}
          height={480}
          className="w-80 h-160 object-cover rounded-lg shadow-lg"
          priority
        />
      </div>
    </>
  );
}
